import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  AccountTree,
  TrackChanges,
  Person,
  Logout,
  Subscriptions,
  Diamond,
  Email,
} from '@mui/icons-material';
import { Sun, Moon } from 'lucide-react';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useThemeMode } from '../contexts/ThemeContext';

const Navbar: React.FC = () => {
  const { user, logout, loading } = useAuth();
  const { isPremium } = useSubscription();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Temporary debugging
  console.log('Navbar state:', { user: !!user, userName: user?.name, loading });

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    handleClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigationGroups = [
    {
      title: 'Overview',
      items: [
        { text: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
      ]
    },
    {
      title: 'Health Management',
      items: [
        { text: 'Health Tree', path: '/talent-tree', icon: <AccountTree /> },
        { text: 'Weekly Commitment', path: '/weekly-lockin', icon: <TrackChanges /> },
      ]
    }
  ];
  
  const allNavigationItems = navigationGroups.flatMap(group => group.items);

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ p: 3, borderBottom: mode === 'dark' ? '1px solid rgba(55, 65, 81, 1)' : '1px solid rgba(229, 231, 235, 1)' }}>
        <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <img 
            src="/Health-Tree.svg" 
            alt="Health-Tree Logo" 
            style={{ 
              width: 24, 
              height: 24,
            }} 
          />
          Health-Tree
        </Typography>
      </Box>
      
      {/* Theme Toggle in Mobile Menu */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <Sun size={16} style={{ color: theme.palette.text.secondary }} />
          <Switch
            checked={mode === 'dark'}
            onCheckedChange={toggleTheme}
          />
          <Moon size={16} style={{ color: theme.palette.text.secondary }} />
          <Label className="sr-only">
            Toggle theme mode
          </Label>
        </Box>
      </Box>
      
      {navigationGroups.map((group, groupIndex) => (
        <Box key={group.title} sx={{ mb: 2 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              px: 2, 
              color: mode === 'dark' ? '#9ca3af' : '#6b7280',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem'
            }}
          >
            {group.title}
          </Typography>
          <List sx={{ pt: 1 }}>
            {group.items.map((item) => (
              <ListItem
                key={item.text}
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                sx={{
                  color: location.pathname === item.path ? '#10b981' : (mode === 'dark' ? '#d1d5db' : '#6b7280'),
                  backgroundColor: location.pathname === item.path ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                  borderRadius: '6px',
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10b981',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{ 
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed"
        elevation={0}
        sx={{ 
          backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
          color: mode === 'dark' ? '#ffffff' : '#111827',
          borderBottom: 'none',
          boxShadow: 'none !important',
          zIndex: 1300,
          backdropFilter: 'none',
          transition: 'none',
          '&::before': {
            display: 'none'
          },
          '&::after': {
            display: 'none'
          }
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 } }}>
          {/* Left Section - Logo and Navigation (20% left from center) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'flex-start', ml: { xs: 0, md: '7%' } }}>
            {user && isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                color: mode === 'dark' ? '#ffffff' : '#000000',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <img 
                src="/Health-Tree.svg" 
                alt="Health-Tree Logo" 
                style={{ 
                  width: 28, 
                  height: 28,
                }} 
              />
              <strong>Health-Tree</strong>
            </Typography>

            {user && !isMobile && (
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                {allNavigationItems.map((item) => (
                  <Button
                    key={item.text}
                    component={Link}
                    to={item.path}
                    color="inherit"
                    size="small"
                    sx={{
                      color: location.pathname === item.path ? '#10b981' : (mode === 'dark' ? '#ffffff' : '#000000'),
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 1,
                      py: 1,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#10b981',
                      },
                    }}
                  >
                    <strong>{item.text}</strong>
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          {/* Right Section - Theme Toggle and User Controls (5% toward middle) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', mr: { xs: 0, md: '5%' } }}>
            {/* Theme Toggle Switch */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Sun size={16} style={{ color: theme.palette.text.secondary }} />
            <Switch
              checked={mode === 'dark'}
              onCheckedChange={toggleTheme}
            />
            <Moon size={16} style={{ color: theme.palette.text.secondary }} />
            <Label className="sr-only">
              Toggle theme mode
            </Label>
          </Box>

          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isPremium ? (
                <Chip
                  icon={<Diamond sx={{ color: 'white !important' }} />}
                  label="Premium"
                  size="small"
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/subscription')}
                  sx={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    px: 2,
                    py: 0.5,
                    minWidth: 'auto',
                    borderRadius: '6px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#059669',
                    },
                  }}
                >
                  Upgrade to Premium
                </Button>
              )}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, backgroundColor: 'primary.main', color: 'white' }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: mode === 'dark' 
                      ? '#111827' 
                      : '#ffffff',
                    border: mode === 'dark' 
                      ? '1px solid rgba(55, 65, 81, 1)' 
                      : '1px solid rgba(229, 231, 235, 1)',
                    boxShadow: mode === 'dark' 
                      ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)' 
                      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    borderRadius: '8px',
                  },
                }}
              >
                <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                  <Person sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem onClick={() => { navigate('/subscription'); handleClose(); }}>
                  <Subscriptions sx={{ mr: 1 }} />
                  {isPremium ? 'Manage Subscription' : 'Upgrade to Premium'}
                </MenuItem>
                <MenuItem onClick={() => { navigate('/contact'); handleClose(); }}>
                  <Email sx={{ mr: 1 }} />
                  Contact Us
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          ) : loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={24} sx={{ color: 'primary.main' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" component={Link} to="/login">
                Login
              </Button>
              <Button variant="contained" component={Link} to="/register">
                Sign Up
              </Button>
            </Box>
          )}
          </Box>
        </Toolbar>
      </AppBar>

      {user && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
              borderRight: mode === 'dark' 
                ? '1px solid rgba(55, 65, 81, 1)' 
                : '1px solid rgba(229, 231, 235, 1)',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Navbar; 